import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PaintBrush, FloppyDisk } from "@phosphor-icons/react";
import { useBrand } from "@/context/BrandContext";

const emptyForm = {
  product_name: "",
  slogan: "",
  website_title: "",
  website_url: "",
};

export default function AdminBranding() {
  const { reload: reloadBrand } = useBrand();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [logo, setLogo] = useState(null);
  const [loginLogo, setLoginLogo] = useState(null);
  const [favicon, setFavicon] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loginLogoPreview, setLoginLogoPreview] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [clearLogo, setClearLogo] = useState(false);
  const [clearLoginLogo, setClearLoginLogo] = useState(false);
  const [clearFavicon, setClearFavicon] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/branding");
      setForm({
        product_name: data.product_name || "",
        slogan: data.slogan || "",
        website_title: data.website_title || "",
        website_url: data.website_url || "",
      });
      setLogoPreview(data.logo_url || null);
      setLoginLogoPreview(data.login_logo_url || null);
      setFaviconPreview(data.favicon_url || null);
      setLogo(null);
      setLoginLogo(null);
      setFavicon(null);
      setClearLogo(false);
      setClearLoginLogo(false);
      setClearFavicon(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onFile = (file, setFile, setPreview, clearFlagSetter) => {
    setFile(file || null);
    clearFlagSetter(false);
    if (file) setPreview(URL.createObjectURL(file));
  };

  const save = async () => {
    if (!form.product_name.trim() || !form.website_title.trim()) {
      toast.error("Product name and web title are required.");
      return;
    }
    setSaving(true);
    try {
      const body = new FormData();
      body.append("product_name", form.product_name.trim());
      body.append("slogan", form.slogan.trim());
      body.append("website_title", form.website_title.trim());
      body.append("website_url", form.website_url.trim());
      if (logo) body.append("logo", logo);
      if (loginLogo) body.append("login_logo", loginLogo);
      if (favicon) body.append("favicon", favicon);
      if (clearLogo) body.append("clear_logo", "1");
      if (clearLoginLogo) body.append("clear_login_logo", "1");
      if (clearFavicon) body.append("clear_favicon", "1");
      const { data } = await api.put("/settings/branding", body);
      setForm({
        product_name: data.product_name || "",
        slogan: data.slogan || "",
        website_title: data.website_title || "",
        website_url: data.website_url || "",
      });
      setLogoPreview(data.logo_url || null);
      setLoginLogoPreview(data.login_logo_url || null);
      setFaviconPreview(data.favicon_url || null);
      setLogo(null);
      setLoginLogo(null);
      setFavicon(null);
      setClearLogo(false);
      setClearLoginLogo(false);
      setClearFavicon(false);
      await reloadBrand();
      toast.success("Branding saved — applied on platform hosts.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-branding-page">
      <PageHeader
        title="Branding"
        subtitle="Main product name, slogan, web title, website URL, logo, and favicon shown on platform hosts (e.g. dev.wacalls.com). Reseller white-label domains use their own branding."
      />

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="rounded-xl border border-border bg-card card-shadow p-5 space-y-4" data-testid="admin-branding-form">
          <div className="flex items-center gap-2">
            <PaintBrush className="w-5 h-5 text-primary" weight="duotone" />
            <h3 className="font-semibold text-base">Platform brand</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Main product name</Label>
              <Input
                data-testid="brand-product-name"
                value={form.product_name}
                onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                placeholder="WA Calls"
                maxLength={80}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Slogan</Label>
              <Input
                data-testid="brand-slogan"
                value={form.slogan}
                onChange={(e) => setForm({ ...form, slogan: e.target.value })}
                placeholder="AI Voice Agents"
                maxLength={120}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Web title</Label>
              <Input
                data-testid="brand-website-title"
                value={form.website_title}
                onChange={(e) => setForm({ ...form, website_title: e.target.value })}
                placeholder="WA Calls — AI Voice Agents"
                maxLength={120}
              />
              <p className="text-[11px] text-muted-foreground">Browser tab title.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Website URL</Label>
              <Input
                data-testid="brand-website-url"
                value={form.website_url}
                onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                placeholder="https://wacalls.com"
                maxLength={200}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Logo</Label>
              <Input
                data-testid="brand-logo"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                onChange={(e) => onFile(e.target.files?.[0], setLogo, setLogoPreview, setClearLogo)}
              />
              <p className="text-[11px] text-muted-foreground">Used in nav, sidebar, and light backgrounds.</p>
              {logoPreview && !clearLogo && (
                <div className="mt-2 flex items-center gap-3">
                  <img src={logoPreview} alt="Logo preview" className="h-12 object-contain" />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs"
                    onClick={() => { setLogo(null); setLogoPreview(null); setClearLogo(true); }}
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Login / signup logo</Label>
              <Input
                data-testid="brand-login-logo"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                onChange={(e) => onFile(e.target.files?.[0], setLoginLogo, setLoginLogoPreview, setClearLoginLogo)}
              />
              <p className="text-[11px] text-muted-foreground">
                Light or white mark for the purple login/signup panel. Falls back to the main logo if empty.
              </p>
              {loginLogoPreview && !clearLoginLogo && (
                <div className="mt-2 flex items-center gap-3 rounded-lg bg-primary p-3">
                  <img src={loginLogoPreview} alt="Login logo preview" className="h-12 object-contain" />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs text-primary-foreground hover:bg-white/10 hover:text-primary-foreground"
                    onClick={() => { setLoginLogo(null); setLoginLogoPreview(null); setClearLoginLogo(true); }}
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Favicon</Label>
              <Input
                data-testid="brand-favicon"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/x-icon,image/vnd.microsoft.icon,image/svg+xml,.ico"
                onChange={(e) => onFile(e.target.files?.[0], setFavicon, setFaviconPreview, setClearFavicon)}
              />
              {faviconPreview && !clearFavicon && (
                <div className="mt-2 flex items-center gap-3">
                  <img src={faviconPreview} alt="Favicon preview" className="h-8 w-8 object-contain" />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs"
                    onClick={() => { setFavicon(null); setFaviconPreview(null); setClearFavicon(true); }}
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="rounded-lg" data-testid="brand-save-btn">
              <FloppyDisk className="w-4 h-4 mr-1.5" weight="bold" />
              {saving ? "Saving…" : "Save branding"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
